import { createSelector } from "@reduxjs/toolkit";
import Parlay from "../../entities/Parlay";
import PreSelectedParlay from "../../entities/PreSelectedParlay";
import {ParlayStatus, ParlayType, PayoutModel} from "../../constants/PickXEnums";
import moment from "moment";
import {makeSelectGamesByIds, selectGamesByIds, selectPlayerById, selectPlayersByIds} from "../sports/sports.selectors";
import PrizeAsset from "../../entities/PrizeAsset";

export const selectContestsByIds = createSelector(
    (state) => state.pickX?.contests,
    (_, ids) => ids,
    (contests, ids) => contests ? contests.filter(x => ids.includes(x.Id)) : []
);

export const selectSelectedParlayId = state => state.pickX?.selectedParlayId;
export const selectUserParlays = createSelector(
    state => state.pickX?.userParlays,
    parlays => parlays.map(p=>new Parlay(p)));

export const selectActiveAndYesterdayUserParlays = createSelector(
    state => state.pickX?.userParlays,
    parlays => {
        const yesterday = moment().subtract(1, 'days');
        return parlays.map(p=>new Parlay(p)).filter(p=> p.Status === ParlayStatus.Active ||
                                                        moment(p.DateCreated).isAfter(yesterday) ||
                                                        moment(p.DateCompleted).isAfter(yesterday)
        );
});

export const selectUserParlaysOnGoing = createSelector(
    state => state.pickX?.userParlays.filter(p=>p.Status === ParlayStatus.Active && p.parlayType === undefined),
    parlays => parlays?.map(p => new Parlay(p)));

export const selectUserParlaysInLastThirtyDays = createSelector(
    state => {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);

        return state.pickX?.userParlays.filter(parlay => {
            const parlayDate = new Date(parlay.DateCreated);
            return (parlayDate >= thirtyDaysAgo && parlayDate <= today) && parlay.parlayType === undefined;
        });
    },
    parlays => parlays?.map(p => new Parlay(p)));

export const selectUserParlaysAllTime = createSelector(
    state => state.pickX?.userParlays.filter(p => p.parlayType === undefined),
    parlays => parlays.map(p => new Parlay(p)));

export const selectUserParlayById = createSelector(
    (state, id) => state.pickX.userParlays.find(x=>x.Id === id),
    (parlay) => parlay ? new Parlay(parlay) : null
);
export const selectUserParlayByType = createSelector(
    (state, parlayType) => state.pickX.userParlays.find(x=>x.parlayType === parlayType),
    (parlay) => parlay ? new Parlay(parlay) : null
);
export const selectSelectedParlay = createSelector(
    (state)=> selectUserParlayById(state, state.pickX.selectedParlayId),
    (parlay) => parlay ? new Parlay(parlay) : null
)

export const selectPreSelectedParlayById =
    createSelector(
        (state, id) => state.pickX?.preSelectedParlays.find(x=>x.Id === id),
        parlay => parlay ? new PreSelectedParlay(parlay) : null
    );

export const doesParlayExist = (state, parlayId) => state.pickX.userParlays.findIndex(p => p.Id === parlayId) !== -1;

export const selectContestIdsNotOnLobby = createSelector(
    state=>state.pickX.contests,
    contests=>contests.filter(c=>!c.IsOnLobby).map(c=>c.Id)
    );

export const selectPrizeAssetById = createSelector(
    (state, id) => state.pickX.prizeAssets.find(x => x.Id === id),
    (prizeAsset) => prizeAsset ? new PrizeAsset(prizeAsset) : null
);

export const selectPartnerById = (state, id) => state.pickX.partners.find(x => x.Id === id);

//Selectors Factory

export const makeCanAddPlayerToParlayChecker = (parlayId) => (state) => {
    const parlay = selectUserParlayById(state, parlayId);
    if(!parlay) return ()=>false;

    return (contestId) => {
        const allContestIds = [...parlay.contestIds, contestId];
        const contests = selectContestsByIds(state, allContestIds);
        const players = selectPlayersByIds(state, contests.map(c => c.PlayerId));
        const games  = selectGamesByIds(state, contests.map(c => c.GameId));
        return parlay.canAdd(contestId, contests, players, games);
    };
};
